import React, { forwardRef, useEffect } from 'react';
import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import Textfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
// this could be removed if we move compal controllers to commons package
// eslint-disable-next-line jira/import/no-restricted-paths
import { useCommandPalette } from '../../../../controllers/command-palette';
import { COMPAL_LISTBOX_ID, COMPAL_LISTBOX_ITEM_ID, SEARCH_INPUT_TEST_ID } from '../constants';

type SearchInputProps = React.ComponentProps<typeof TextFieldStyle> & {
	activeId?: string;
};

export const CommandPaletteSearchInput: React.FC<SearchInputProps> = forwardRef(
	({ activeId, ...rest }, ref) => {
		const [{ shouldForceSearchValue, search }, { resetShouldForceSearchValue }] = fg(
			'compal-ktlo-improvements-1',
		)
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useCommandPalette()
			: [{ shouldForceSearchValue: false, search: '' }, { resetShouldForceSearchValue: noop }];

		if (fg('compal-ktlo-improvements-1')) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useEffect(() => {
				if (shouldForceSearchValue) {
					resetShouldForceSearchValue();
				}
			}, [resetShouldForceSearchValue, shouldForceSearchValue]);
		}

		const ariaProps: React.ComponentProps<typeof TextFieldStyle> = !isNil(activeId)
			? {
					'aria-expanded': true,
					'aria-controls': COMPAL_LISTBOX_ID,
					role: 'combobox',
					'aria-activedescendant': `${COMPAL_LISTBOX_ITEM_ID}-${activeId}`,
				}
			: {
					role: 'searchbox',
				};
		if (fg('a11y_violations_fixes')) {
			return (
				<TextFieldStyle
					appearance="none"
					testId={SEARCH_INPUT_TEST_ID}
					ref={ref}
					{...ariaProps}
					{...rest}
					{...(shouldForceSearchValue ? { value: search } : {})}
				/>
			);
		}
		return (
			<TextFieldStyle
				appearance="none"
				testId={SEARCH_INPUT_TEST_ID}
				ref={ref}
				aria-expanded
				aria-controls={COMPAL_LISTBOX_ID}
				aria-activedescendant={
					activeId !== undefined ? `${COMPAL_LISTBOX_ITEM_ID}-${activeId}` : undefined
				}
				role="combobox"
				{...rest}
				{...(shouldForceSearchValue ? { value: search } : {})}
			/>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextFieldStyle = styled(Textfield)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		backgroundColor: 'transparent !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	border: 'none',
	padding: `${token('space.075', '6px')} ${token('space.050', '4px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'input,input:not([data-compact])': {
		font: token('font.body.large'),
		paddingTop: '0px',
		paddingBottom: '0px',
		paddingLeft: `${token('space.050', '4px')}`,
		height: '20px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'input::placeholder': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.subtlest', colors.N200),
	},
});
